import { config } from '../../config/Config';
import WFS from 'ol/format/WFS.js';
import GML from 'ol/format/GML.js';
import { LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import { Feature } from 'ol';
import { toast } from 'react-toastify';
import { getTranslatedKey } from './TranslateUtils';

let currentFetch = null;

export async function getFeatureType(layer) {
  if (currentFetch) {
    currentFetch.abort();
  }

  let name = layer.getExtension().Namespace + ':' + layer.getName();

  let url = `${config.url_owsseg_wfs}?service=wfs&version=${config.version_2_0}&request=GetFeature&typename=${name}&outputFormat=application/json&count=1&startindex=0`;
  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    currentFetch = null;
    return await resp.json();
  } catch (error) {
    if (error.name !== 'AbortError') {
    }
  }
}

export const getGeometryType = async (layer) => {
  let nameSpace = layer.getExtension().Namespace;
  let url = `/ows/rest/workspaces/${nameSpace}/datastores/${nameSpace}/featuretypes/${layer.getName()}.json`;
  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    currentFetch = null;
    let geom;
    const json = await resp.json();
    const atrArray = json.featureType.attributes.attribute;
    atrArray.forEach((attr) => {
      if (attr.name === layer.getExtension().Geometry) [geom] = attr.binding.split('.').slice(-1);
    });
    return geom;
  } catch (error) {
    if (error.name !== 'AbortError') {
    }
  }
};

export const getFeatureFields = async (layer, api) => {
  let name = layer.getExtension().Namespace + ':' + layer.getName();
  const or = layer.getOnlineResource();

  let url = `${or}?REQUEST=DescribeFeatureType&VERSION=1.1.0&TYPENAME=${name}&SERVICE=WFS`;
  let controller = new AbortController();
  let signal = controller.signal;
  currentFetch = controller;

  try {
    let resp = await fetch(url, { signal });
    const text = await resp.text();

    const objArr = [];
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, 'text/xml');

    let complexTypes = xmlDoc.getElementsByTagNameNS('http://www.w3.org/2001/XMLSchema', 'complexType');
    Array.from(complexTypes).forEach((complexType) => {
      const elements = Array.from(complexType.getElementsByTagNameNS('http://www.w3.org/2001/XMLSchema', 'element'));
      elements.forEach((element) => {
        const obj = {};
        const elementName = element.getAttribute('name');
        const elementType = element.getAttribute('type');
        const nullable = element.getAttribute('nillable');

        if (elementName && elementType) {
          obj.field = elementName;
          obj.type = elementType;
          obj.nullable = nullable === 'true' ? true : false;
          objArr.push(obj);
        }
      });
    });
    let result = objArr
      .filter((property) => {
        let extension = layer.getExtension();
        if (!api) {
          return property.field !== extension.Geometry && property.field !== extension.Id;
        } else {
          return true;
        }
      })
      .map((property) => {
        if (property.type) {
          property.type = property.type.replace('xsd:', '').replace('decimal', 'número').replace('string', 'text');
        }
        return property;
      });

    currentFetch = null;
    return result;
  } catch (error) {
    if (error.name !== 'AbortError') {
    }
  }
};

export const createFeature = (layer, source, feature, params) => {
  if (source && source.getFeatures().length > 1) {
    feature = mergeGeom(layer, source, feature);
  }

  let namespace, layerName;
  namespace = 'www.opengeospatial.org/' + layer.getExtension().Namespace;
  layerName = layer.getName();
  feature.getGeometry().transform(config.EPSG_mapa, config.EPSG);
  feature = geometryFormat(layer, feature);

  for (const key in params) {
    if (params[key] === undefined) {
      delete params[key];
    }
  }
  feature.setProperties(params);

  const formatWfs = new WFS();
  const formatGML = new GML({ featureNS: namespace, featureType: layerName, srsName: config.EPSG });

  let transaction = formatWfs.writeTransaction([feature], null, null, formatGML);
  let body = new XMLSerializer().serializeToString(transaction);

  fetch(config.url_owsseg_wfs, {
    method: 'POST',
    body: body
  })
    .then((response) => {
      if (response.ok) {
        const type = response.headers.get('Content-Type');
        if (type === 'application/xml') {
          toast.error(getTranslatedKey('errorAddFeature'));
        } else {
          toast.success(getTranslatedKey('successCreation'));
          if (layer.getOLLayer()) {
            layer.getOLLayer().getSource().updateParams({ time: Date.now() });
          }
        }
      } else {
        toast.error(getTranslatedKey('errorAddFeature'));
      }
    })
    .catch(function (error) {
      toast.error(getTranslatedKey('errorAddFeature'));
    });
};

export const updateFeature = (layer, source, editFeature, params, deleteGeom) => {
  let namespace, layerName, feature;
  feature = editFeature;
  if ((source && source.getFeatures().length > 1) || deleteGeom) {
    feature = mergeGeom(layer, source, editFeature);
  }
  feature = geometryFormat(layer, feature);

  namespace = 'www.opengeospatial.org/' + layer.getExtension().Namespace;
  layerName = layer.getName();
  feature.getGeometry().transform(config.EPSG_mapa, config.EPSG);
  feature.setProperties(params);

  const formatWfs = new WFS();
  const formatGML = new GML({ featureNS: namespace, featureType: layerName, srsName: config.EPSG });
  let transaction = formatWfs.writeTransaction(null, [feature], null, formatGML);
  let body = new XMLSerializer().serializeToString(transaction);
  fetch(config.url_owsseg_wfs, {
    method: 'POST',
    body: body
  })
    .then((response) => {
      if (response.ok) {
        const type = response.headers.get('Content-Type');
        if (type === 'application/xml') {
          toast.error(getTranslatedKey('errorAddFeature'));
        } else {
          toast.success(getTranslatedKey('successCreation'));
          if (layer.getOLLayer()) {
            layer.getOLLayer().getSource().updateParams({ time: Date.now() });
          }
        }
      } else {
        toast.error(getTranslatedKey('errorEditFeature'));
      }
    })
    .catch(function (error) {
      toast.error(getTranslatedKey('errorEditFeature'));
    });
};

export const deleteFeature = (layer, feature) => {
  let clonedFeature = feature.clone();
  clonedFeature.setId(feature.getId());

  let namespace, layerName, geom;
  namespace = 'www.opengeospatial.org/' + layer.getExtension().Namespace;
  layerName = layer.getName();

  geom = clonedFeature.getGeometry();
  geom = geom.transform(config.EPSG_mapa, config.EPSG);
  clonedFeature.setGeometry(geom);

  clonedFeature.setGeometryName(layer.getExtension().Geometry);

  const formatWfs = new WFS();
  const formatGML = new GML({ featureNS: namespace, featureType: layerName, srsName: config.EPSG });

  let transaction = formatWfs.writeTransaction(null, null, [clonedFeature], formatGML);

  let body = new XMLSerializer().serializeToString(transaction);

  fetch(config.url_owsseg_wfs, {
    method: 'POST',
    body: body
  })
    .then((response) => {
      if (response.ok) {
        toast.success(getTranslatedKey('successDelete'));
        if (layer.getOLLayer()) {
          layer.getOLLayer().getSource().updateParams({ time: Date.now() });
        }
      } else {
        toast.error(getTranslatedKey('errorDeleteFeature'));
      }
    })
    .catch(function (error) {
      toast.error(getTranslatedKey('errorDeleteFeature'));
    });
};

export const mergeGeom = (layer, source, ogFeature) => {
  const geometries = [];
  source.getFeatures().forEach((feat) => {
    let geom = feat.getGeometry();
    let coords = geom.getCoordinates();
    if (geom instanceof MultiPolygon || geom instanceof MultiLineString || geom instanceof MultiPoint) {
      coords.forEach((geom) => {
        geometries.push(geom);
      });
    } else {
      geometries.push(coords);
    }
  });
  let multiGeom;
  const ogGeom = ogFeature.getGeometry();
  if (ogGeom instanceof MultiPolygon || ogGeom instanceof Polygon) {
    multiGeom = new MultiPolygon(geometries);
  } else if (ogGeom instanceof MultiLineString || ogGeom instanceof LineString) {
    multiGeom = new MultiLineString(geometries);
  } else if (ogGeom instanceof MultiPoint || ogGeom instanceof Point) {
    multiGeom = new MultiPoint(geometries);
  }

  const merged = new Feature({
    geometry: multiGeom
  });

  let ogId = ogFeature.getId();
  merged.setId(ogId);
  const config = {};
  for (let key in ogFeature.values_) {
    if (key !== layer.getExtension().Geometry) {
      config[key] = ogFeature.values_[key];
    }
  }

  return merged;
};

const geometryFormat = (layer, feature) => {
  const newGeom = feature.getGeometry();
  feature.setGeometryName(layer.getExtension().Geometry);
  feature.setGeometry(newGeom);
  feature.unset('geometry');
  return feature;
};

export const splitGeom = (feature) => {
  const geometry = feature.getGeometry();

  if (geometry instanceof MultiPoint || geometry instanceof MultiPolygon || geometry instanceof MultiLineString) {
    const coordinates = geometry.getCoordinates();
    let newGeometries = [];
    for (let i = 0; i < coordinates.length; i++) {
      let newGeometry;
      if (geometry instanceof MultiPoint) {
        newGeometry = new Point(coordinates[i]);
      } else if (geometry instanceof MultiPolygon) {
        newGeometry = new Polygon(coordinates[i]);
      } else {
        newGeometry = new LineString(coordinates[i]);
      }
      newGeometries.push(new Feature({ geometry: newGeometry }));
    }
    return newGeometries;
  } else {
    toast.error(getTranslatedKey('errorEditFeature'));
  }
};
